import { type AuthenticatedSession } from '@/api/schemas/session'

/**
 * Realm roles arriving via the session (KeycloakRoleConverter). The service
 * serializes them WITHOUT the Spring-internal "ROLE_" prefix (verified
 * against the live /auth/session response — a session that previously
 * carried "ROLE_PLATFORM_ADMIN" actually sends "PLATFORM_ADMIN"). Match the
 * raw string here; never re-add the prefix.
 * Roles gate routes/sections; entitlements gate features — never mix the two
 * (UI-RECOMMENDATIONS §5.2, §5.10).
 */
export const ROLES = {
  TENANT_ADMIN: 'TENANT_ADMIN',
  TENANT_BILLING_ADMIN: 'TENANT_BILLING_ADMIN',
  TENANT_SUPPORT: 'TENANT_SUPPORT',
  /** Product-level staff variants (see @PreAuthorize in AdminController). */
  SUPPORT: 'SUPPORT',
  BILLING_ADMIN: 'BILLING_ADMIN',
  /** FenceAuth internal staff. PLATFORM_ADMIN is the only platform-staff
   * full-access role — the legacy bare ADMIN role has been removed. */
  PLATFORM_ADMIN: 'PLATFORM_ADMIN',
  PLATFORM_SUPPORT: 'PLATFORM_SUPPORT',
  PLATFORM_BILLING: 'PLATFORM_BILLING',
} as const

export type Role = (typeof ROLES)[keyof typeof ROLES]

export const TENANT_PORTAL_ROLES: readonly Role[] = [
  ROLES.TENANT_ADMIN,
  ROLES.TENANT_BILLING_ADMIN,
  ROLES.TENANT_SUPPORT,
  ROLES.SUPPORT,
  ROLES.BILLING_ADMIN,
]

export const PLATFORM_ROLES: readonly Role[] = [
  ROLES.PLATFORM_ADMIN,
  ROLES.PLATFORM_SUPPORT,
  ROLES.PLATFORM_BILLING,
]

export function hasAnyRole(session: AuthenticatedSession | null, roles: readonly Role[]): boolean {
  if (!session) return false
  return roles.some((role) => session.roles.includes(role))
}

export function isPlatformStaff(session: AuthenticatedSession | null): boolean {
  return hasAnyRole(session, PLATFORM_ROLES)
}

export function isTenantMember(session: AuthenticatedSession | null): boolean {
  return hasAnyRole(session, TENANT_PORTAL_ROLES)
}

/** Post-login destination by role (UI-REQUIREMENTS P1.3). */
export function defaultHomeFor(session: AuthenticatedSession): string {
  if (isPlatformStaff(session)) return '/admin'
  if (isTenantMember(session)) return '/dashboard'
  if (session.tenantSlug) return `/portal/${session.tenantSlug}/account`
  return '/'
}
